import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import { InputField } from "@/components/InputField/InputField";
import { useSearch } from "@/utils";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useGetUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { FilterDropdown } from "@/components/FilterDropdown.tsx/FilterDropdown";

import SortDropDown, { SORT_BY_ALL } from "../../ManageToDos/components/SortDropDown";
import { ToDoType } from "../enum/ToDoType.enum";
import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";

type WithTag = {
	tag?: { id?: number; name?: string } | null;
};

type WithAssignee = {
	assigneeId?: number | null;
	assignee?: { id?: number; firstName?: string; lastName?: string } | null;
};

interface ToDoFilterPanelProps {
	data?: (WithTag & WithAssignee)[];
	activeTabTagId: number;
	viewMode?: string;
	onFilterClick: (tagId: number) => void;
	isSortEnabled?: boolean;
	sortType?: ToDoType | typeof SORT_BY_ALL;
	onSort?: (sortType: ToDoType | typeof SORT_BY_ALL) => void;
	sortOptions?: ToDoType[];
	activeAssigneeId?: number | "unassigned" | null;
	onAssigneeFilter?: (userId: number | "unassigned" | null) => void;
	onSearch?: (search: string) => void;
}

export default function ToDoFilterPanel({
	data,
	viewMode = "column",
	activeTabTagId,
	onFilterClick,
	activeAssigneeId,
	onAssigneeFilter,
	isSortEnabled,
	sortType,
	onSearch,
	onSort,
	sortOptions,
}: ToDoFilterPanelProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);

	const [showAllTags, setShowAllTags] = useState(false);
	const [searchValue, setSearchValue] = useState<string | null>(null);

	const tagsCount = useMemo(() => {
		const tagCounts = new Map<number, { tagId: number; name: string; count: number }>();

		data?.forEach((item) => {
			const tag = item?.tag;

			if (tag && tag.id) {
				const existingTag = tagCounts.get(tag.id);

				if (existingTag) {
					existingTag.count += 1;
				} else {
					tagCounts.set(tag.id, { tagId: tag.id, count: 1, name: tag.name ?? "" });
				}
			}
		});

		return tagCounts;
	}, [data]);

	const items = useMemo(() => {
		return Array.from(tagsCount.values())
			.sort((a, b) => b.count - a.count)
			.slice(0, showAllTags ? undefined : 5);
	}, [tagsCount, showAllTags]);

	const { data: users } = useGetAssigneesQuery();

	const assigneeOptions = [
		...(users?.map((user) => ({
			firstName: user.firstName,
			lastName: user.lastName,
			assigneeId: typeof user.id === "number" ? user.id : parseInt(String(user.id), 10),
		})) ?? []),
	];

	function handleFilter(val: string) {
		setSearchValue(val);
		onSearch?.(val);
	}

	return (
		<div className="w-full flex flex-col">
			<div className="flex justify-between">
				<div className="flex flex-row w-[350px]">
					<InputField
						className="px-4 !py-0 !h-[40px]"
						handleChange={(e) => handleFilter(e.target.value)}
						name="search"
						placeholder={ts("search")}
						value={searchValue ?? ""}
					/>
				</div>
				{onAssigneeFilter && (
					<div className="flex flex-row justify-between">
						{onAssigneeFilter && (
							<div className="flex flex-row w-fit">
								<FilterDropdown
									className="min-w-[250px]"
									data={[
										{ id: null, title: t("createTask.filterByAssignee") },
										{ id: "unassigned", title: t("createTask.unassigned") ?? "Unassigned" },
										...assigneeOptions.map((a) => ({
											id: a.assigneeId,
											title: `${a.firstName} ${a.lastName}`,
										})),
									]}
									floating={true}
									handleSelect={(item) => {
										if (item.id === null) {
											onAssigneeFilter(null);
										} else if (item.id === "unassigned") {
											onAssigneeFilter("unassigned");
										} else {
											onAssigneeFilter(Number(item.id));
										}
									}}
									size={ButtonSize.M}
									value={
										activeAssigneeId != null
											? {
													id: activeAssigneeId,
													title: (() => {
														if (activeAssigneeId === "unassigned") {
															return t("createTask.unassigned") ?? "Unassigned";
														}

														const a = assigneeOptions.find(
															(a) => a.assigneeId === activeAssigneeId
														);

														return a ? `${a.firstName} ${a.lastName}` : "";
													})(),
											  }
											: undefined
									}
								/>
							</div>
						)}{" "}
					</div>
				)}{" "}
			</div>
			<div className="flex flex-row items-center">
				{viewMode === "column" ? (
					<div className="flex gap-1 flex-col items-start">
						<div className="flex flex-row gap-1 flex-wrap items-center">
							<button
								key={-1}
								className={`flex w-fit p-3 ${
									activeTabTagId === -1
										? "border-b-2 border-primary-blue font-semibold text-primary-blue"
										: "font-medium text-[#777D8D]"
								} text-[14px] leading-5 items-center justify-center gap-[6px]`}
								type="button"
								onClick={() => onFilterClick(-1)}
							>
								{ts("all")}
								<span
									className={`border leading-4 border-primary-gray-lightest ${
										activeTabTagId === -1
											? "bg-primary-blue text-white"
											: "bg-primary-gray-lightest"
									} rounded-[4px] w-5 h-5 flex items-center justify-center text-[#777D8D] text-[12px] font-medium`}
								>
									{data?.length ?? "0"}
								</span>
							</button>

							{items.map((item) => (
								<button
									key={item.tagId}
									className={`flex w-fit ${
										activeTabTagId === item.tagId
											? "border-b-2 border-primary-blue font-semibold text-primary-blue"
											: "font-medium text-[#777D8D]"
									} p-3 text-[14px] leading-5 items-center justify-center gap-[6px]`}
									type="button"
									onClick={() => onFilterClick(item.tagId)}
								>
									{item.name}
									<span
										className={`border leading-4 border-primary-gray-lightest ${
											activeTabTagId === item.tagId
												? "bg-primary-blue text-white"
												: "bg-primary-gray-lightest"
										} rounded-[4px] w-5 h-5 flex items-center justify-center text-[#777D8D] text-[12px] font-medium`}
									>
										{item.count ?? "0"}
									</span>
								</button>
							))}
						</div>
						{tagsCount.size > 5 ? (
							<div className="flex flex-row items-center">
								<Button
									className="text-sm text-[#777D8D] mt-4 border-b-2 border-primary-gray-lightest"
									color={ButtonColor.ACTION_SECONDARY}
									size={ButtonSize.XS}
									title={showAllTags ? ts("show-less") : ts("show-more")}
									onClick={() => setShowAllTags(!showAllTags)}
								/>
							</div>
						) : (
							""
						)}
					</div>
				) : null}

				{isSortEnabled && sortType && (
					<SortDropDown
						className="!ml-auto"
						options={sortOptions ?? []}
						value={sortType}
						onChange={(e) => onSort?.(e.target.value as unknown as ToDoType | typeof SORT_BY_ALL)}
					/>
				)}
			</div>
		</div>
	);
}
